import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { pipelineService } from '../services/pipelineService';
import { apiGet, apiPost } from '../services/api';
import { useSettings } from './SettingsContext';

const PipelineContext = createContext();

export const usePipeline = () => {
  const context = useContext(PipelineContext);
  if (!context) {
    throw new Error('usePipeline must be used within a PipelineProvider');
  }
  return context;
};

export const PipelineProvider = ({ children }) => {
  const { pipelineSettings, isLoading: settingsLoading } = useSettings();
  const [settings, setSettings] = useState({
    // Execution defaults
    default_retry_count: 3,
    default_timeout: 1800,
    log_level: 'INFO',
    auto_refresh_interval: 30,
    
    // Notifications
    enable_notifications: true,
    notification_on_success: false,
    notification_on_failure: true,
    
    // Logging and cleanup
    auto_cleanup_logs: true,
    log_retention_days: 30,
    
    // Performance
    max_concurrent_pipelines: 5,
    enable_pipeline_analytics: true,
    enable_performance_monitoring: true,
    
    // Spark defaults
    default_spark_memory: '2g',
    default_spark_cores: 2,
    enable_auto_scaling: false,
    
    // Debugging
    pipeline_priority: 'normal',
    enable_debugging: false,
    debug_log_level: 'DEBUG'
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [runningPipelines, setRunningPipelines] = useState([]);
  const [pipelineStats, setPipelineStats] = useState({
    total: 0,
    running: 0,
    succeeded: 0,
    failed: 0,
    queued: 0
  });

  // Load pipeline settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && pipelineSettings) {
      setSettings(prev => ({
        ...prev,
        ...pipelineSettings
      }));
      setIsLoading(false);
    }
  }, [pipelineSettings, settingsLoading]);

  // Load stats
  useEffect(() => {
    if (!isLoading) {
      loadPipelineStats();
    }
  }, [isLoading]);

  const loadPipelineStats = async () => {
    try {
      // Get pipeline statistics from backend with authentication
      const stats = await apiGet('pipelines/stats');
      setPipelineStats(stats);
    } catch (error) {
      logger.warn('Could not load pipeline stats:', error);
    }
  };

  // Update pipeline settings
  const updatePipelineSettings = async (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // Save to backend
      await settingsService.updateSettingsBulk('pipeline', updatedSettings);
      
      // Apply settings to running pipelines if applicable
      await applySettingsToRunningPipelines(newSettings);
    } catch (error) {
      logger.error('Failed to save pipeline settings:', error);
      // Revert on error
      await loadPipelineSettings();
    }
  };

  // Apply settings to running pipelines
  const applySettingsToRunningPipelines = async (newSettings) => {
    try {
      // Update log levels for running pipelines
      if (newSettings.log_level) {
        await apiPost('pipelines/running/log-level', { 
          logLevel: newSettings.log_level 
        });
      }
      
      // Update monitoring settings
      if (newSettings.enable_performance_monitoring !== undefined) {
        await apiPost('pipelines/running/monitoring', { 
          enabled: newSettings.enable_performance_monitoring 
        });
      }
    } catch (error) {
      logger.warn('Could not apply settings to running pipelines:', error);
    }
  };

  // Get default pipeline configuration
  const getDefaultPipelineConfig = useCallback(() => {
    return {
      retryCount: settings.default_retry_count,
      timeout: settings.default_timeout,
      logLevel: settings.log_level,
      sparkConfig: {
        memory: settings.default_spark_memory,
        cores: settings.default_spark_cores,
        autoScaling: settings.enable_auto_scaling
      },
      monitoring: {
        enabled: settings.enable_performance_monitoring,
        analytics: settings.enable_pipeline_analytics
      },
      notifications: {
        enabled: settings.enable_notifications,
        onSuccess: settings.notification_on_success,
        onFailure: settings.notification_on_failure
      },
      debugging: {
        enabled: settings.enable_debugging,
        logLevel: settings.debug_log_level
      },
      priority: settings.pipeline_priority
    };
  }, [settings]);

  // Create pipeline with default settings
  const createPipelineWithDefaults = async (pipelineData) => {
    const defaultConfig = getDefaultPipelineConfig();
    const pipelineWithDefaults = {
      ...pipelineData,
      config: {
        ...defaultConfig,
        ...pipelineData.config
      }
    };
    
    try {
      const result = await pipelineService.createPipeline(pipelineWithDefaults);
      await loadPipelineStats(); // Refresh stats
      return result;
    } catch (error) {
      logger.error('Failed to create pipeline:', error);
      throw error;
    }
  };

  // Execute pipeline with settings applied
  const executePipelineWithSettings = async (pipelineId, overrides = {}) => {
    const defaultConfig = getDefaultPipelineConfig();
    const executionConfig = {
      ...defaultConfig,
      ...overrides
    };

    try {
      const result = await pipelineService.executePipeline(pipelineId, executionConfig);
      
      // Update running pipelines list
      setRunningPipelines(prev => [...prev, { 
        id: pipelineId, 
        startTime: new Date().toISOString(),
        config: executionConfig
      }]);
      
      await loadPipelineStats(); // Refresh stats
      return result;
    } catch (error) {
      logger.error('Failed to execute pipeline:', error);
      throw error;
    }
  };

  // Monitor pipeline execution
  const monitorPipelineExecution = useCallback(async (pipelineId) => {
    if (!settings.enable_performance_monitoring) return null;
    
    try {
      const response = await apiGet(`pipelines/${pipelineId}/monitoring`);
      return response;
    } catch (error) {
      logger.warn('Failed to get pipeline monitoring data:', error);
    }
    return null;
  }, [settings.enable_performance_monitoring]);

  // Cleanup old logs based on settings
  const cleanupOldLogs = useCallback(async () => {
    if (!settings.auto_cleanup_logs) return;
    
    try {
      await apiPost('pipelines/logs/cleanup', { 
        retentionDays: settings.log_retention_days 
      });
    } catch (error) {
      logger.warn('Failed to cleanup old logs:', error);
    }
  }, [settings.auto_cleanup_logs, settings.log_retention_days]);

  // Set up auto-refresh for pipeline stats
  useEffect(() => {
    if (settings.auto_refresh_interval > 0) {
      const interval = setInterval(() => {
        loadPipelineStats();
      }, settings.auto_refresh_interval * 1000);
      
      return () => clearInterval(interval);
    }
  }, [settings.auto_refresh_interval]);

  // Set up log cleanup schedule
  useEffect(() => {
    if (settings.auto_cleanup_logs) {
      // Run cleanup daily
      const interval = setInterval(cleanupOldLogs, 24 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [settings.auto_cleanup_logs, cleanupOldLogs]);

  // Get pipeline recommendations based on settings and stats
  const getPipelineRecommendations = useCallback(() => {
    const recommendations = [];

    // Check concurrent pipeline limit
    if (pipelineStats.running >= settings.max_concurrent_pipelines) {
      recommendations.push({
        type: 'warning',
        category: 'performance',
        title: 'Pipeline Limit Reached',
        description: `You have ${pipelineStats.running} pipelines running (limit: ${settings.max_concurrent_pipelines}). Consider increasing the limit or waiting for some to complete.`,
        action: 'increase_pipeline_limit'
      });
    }

    // Check failure rate
    const failureRate = pipelineStats.total > 0 ? (pipelineStats.failed / pipelineStats.total) * 100 : 0;
    if (failureRate > 20) {
      recommendations.push({
        type: 'error',
        category: 'reliability',
        title: 'High Failure Rate',
        description: `${failureRate.toFixed(1)}% of pipelines are failing. Consider enabling debugging or reviewing pipeline configurations.`,
        action: 'enable_debugging'
      });
    }

    // Memory recommendations
    if (settings.default_spark_memory === '1g' && pipelineStats.total > 10) {
      recommendations.push({
        type: 'info',
        category: 'performance',
        title: 'Consider Increasing Spark Memory',
        description: 'For better performance with larger datasets, consider increasing default Spark memory from 1g to 2g or higher.',
        action: 'increase_spark_memory'
      });
    }

    return recommendations;
  }, [settings, pipelineStats]);

  // Apply recommendation
  const applyRecommendation = useCallback(async (action) => {
    switch (action) {
      case 'increase_pipeline_limit':
        await updatePipelineSettings({ max_concurrent_pipelines: settings.max_concurrent_pipelines + 2 });
        break;
      case 'enable_debugging':
        await updatePipelineSettings({ enable_debugging: true, debug_log_level: 'DEBUG' });
        break;
      case 'increase_spark_memory':
        await updatePipelineSettings({ default_spark_memory: '4g' });
        break;
      default:
        logger.warn('Unknown recommendation action:', action);
    }
  }, [settings, updatePipelineSettings]);

  const value = {
    settings,
    isLoading,
    runningPipelines,
    pipelineStats,
    recommendations: getPipelineRecommendations(),
    
    // Settings management
    updatePipelineSettings,
    
    // Pipeline operations with settings
    getDefaultPipelineConfig,
    createPipelineWithDefaults,
    executePipelineWithSettings,
    monitorPipelineExecution,
    
    // Utilities
    loadPipelineStats,
    cleanupOldLogs,
    applyRecommendation
  };

  return (
    <PipelineContext.Provider value={value}>
      {children}
    </PipelineContext.Provider>
  );
};

export default PipelineContext;